import { useEffect, useState, useMemo } from "react";
import { Plus, Pencil, Trash2, ListChecks } from "lucide-react";
import {
  listClients,
  listDeadlines,
  createDeadline,
  updateDeadline,
  deleteDeadline,
  TAX_TYPES,
  formatBRDate,
  statusRowClass,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const emptyForm = {
  client_id: "",
  tax_type: "",
  description: "",
  due_date: "",
  status: "pending",
};

export default function Deadlines() {
  const [clients, setClients] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const [c, d] = await Promise.all([listClients(), listDeadlines()]);
      setClients(c);
      setDeadlines(d);
    } catch {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const clientMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c])),
    [clients]
  );

  const openNew = () => {
    if (clients.length === 0) {
      toast.error("Cadastre um cliente antes de criar prazos");
      return;
    }
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      client_id: d.client_id,
      tax_type: d.tax_type,
      description: d.description || "",
      due_date: d.due_date,
      status: d.status,
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.tax_type || !form.due_date) {
      toast.error("Cliente, tributo e data são obrigatórios");
      return;
    }
    try {
      if (editing) {
        await updateDeadline(editing.id, form);
        toast.success("Prazo atualizado");
      } else {
        await createDeadline(form);
        toast.success("Prazo cadastrado");
      }
      setDialogOpen(false);
      load();
    } catch {
      toast.error("Erro ao salvar prazo");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteDeadline(confirmDelete.id);
      toast.success("Prazo excluído");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  return (
    <div className="space-y-6" data-testid="deadlines-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]">
            Prazos Fiscais
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">
            Cadastre prazos e vincule a clientes específicos.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNew}
              data-testid="add-deadline-button"
              className="bg-[#0F2942] hover:bg-[#173E63] text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Prazo
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar Prazo" : "Novo Prazo"}</DialogTitle>
              <DialogDescription>
                Vincule um tributo a um cliente com data de vencimento.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Cliente *</Label>
                <Select
                  value={form.client_id}
                  onValueChange={(v) => setForm({ ...form, client_id: v })}
                >
                  <SelectTrigger data-testid="deadline-client-select" className="mt-1">
                    <SelectValue placeholder="Selecione um cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tributo *</Label>
                <Select
                  value={form.tax_type}
                  onValueChange={(v) => setForm({ ...form, tax_type: v })}
                >
                  <SelectTrigger data-testid="deadline-tax-select" className="mt-1">
                    <SelectValue placeholder="Selecione o tributo" />
                  </SelectTrigger>
                  <SelectContent>
                    {TAX_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="description">Descrição (opcional)</Label>
                <Input
                  id="description"
                  data-testid="deadline-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Ex: Competência 02/2026"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="due_date">Data de vencimento *</Label>
                <Input
                  id="due_date"
                  type="date"
                  data-testid="deadline-date-input"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              {editing && (
                <div>
                  <Label>Status</Label>
                  <Select
                    value={form.status}
                    onValueChange={(v) => setForm({ ...form, status: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="deadline-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="completed">Concluído</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  data-testid="deadline-cancel-button"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="deadline-submit-button"
                  className="bg-[#0F2942] hover:bg-[#173E63] text-white"
                >
                  {editing ? "Salvar" : "Cadastrar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm">Carregando...</div>
        ) : deadlines.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-deadlines-list">
            <ListChecks className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Nenhum prazo cadastrado ainda.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="deadlines-config-table">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-semibold px-4 py-3">Cliente</th>
                  <th className="text-left font-semibold px-4 py-3">Tributo</th>
                  <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">
                    Descrição
                  </th>
                  <th className="text-left font-semibold px-4 py-3">Vencimento</th>
                  <th className="text-left font-semibold px-4 py-3">Status</th>
                  <th className="text-right font-semibold px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {deadlines.map((d) => (
                  <tr
                    key={d.id}
                    className={`${statusRowClass(d)} border-b border-slate-100 last:border-0`}
                    data-testid={`deadline-config-row-${d.id}`}
                  >
                    <td className="px-4 py-3 font-medium text-[#0F172A]">
                      {clientMap[d.client_id]?.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{d.tax_type}</td>
                    <td className="px-4 py-3 text-slate-500 hidden md:table-cell">
                      {d.description || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {formatBRDate(d.due_date)}
                    </td>
                    <td className="px-4 py-3">
                      {d.status === "completed" ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-0">
                          Concluído
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-0">
                          Pendente
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(d)}
                          data-testid={`edit-deadline-${d.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmDelete(d)}
                          data-testid={`delete-deadline-${d.id}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir prazo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-deadline-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="confirm-delete-deadline"
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
